/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Inject, Injectable, Optional} from '@angular/core';
import * as _ from 'lodash';

import {WINDOW} from '../../app/angular-platform-providers';
import {ApiAuthService} from '../../app/sign-in/api-auth.service';
import {AUTH_EVENT} from './auth-constants';
import {Session} from '../session/session';
import {UpdateSession} from '../session/update-session';

type RefreshSessionSnapshot = {
  accessToken: string;
  refreshToken: string;
  scope: string;
  username: string;
  masqueraderUsername?: string;
  twoFactorAuthType?: string;
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private authRefreshPromise: any;

  constructor(
    private readonly apiAuth: ApiAuthService,
    @Optional() @Inject(WINDOW) private readonly window?: Window
  ) {
  }

  authenticate(username: any, password: any, masqUsername?: any): any {
    return this.apiAuth.authenticate(username, password, masqUsername);
  }

  twoFactorAuth(username: any, twoFactorAuthCode: any): any {
    return this.apiAuth.twoFactorAuth(twoFactorAuthCode, Session.obtainData().accessToken).then((res: any) => {
      return Session.obtain().init(res.scope, res.access_token, res.refresh_token, res.username, res.expires_in, res.masquerader_username, undefined, res.auth_type);
    });
  }

  getSession(): any {
    return Session.obtain();
  }

  getSessionData(): any {
    return Session.obtainData();
  }

  isValidSession(): boolean {
    const session = Session.obtainData();
    return _.isObject(session) && !_.isEmpty(session);
  }

  isAuthenticated(): boolean {
    const session: any = Session.obtainData();
    if (!session || _.isEmpty(session)) {
      return false;
    }
    return !_.isEmpty(session.scope) && session.scope === 'NG_API';
  }

  refresh(): any {
    const requestedSession = Session.obtainData() as RefreshSessionSnapshot;
    if (!this.isAuthenticated() || !requestedSession.refreshToken) {
      return Promise.reject(AUTH_EVENT.notAuthenticated);
    }

    if (!this.authRefreshPromise) {
      this.authRefreshPromise = this.refreshWithCrossTabLock(requestedSession)
        .finally(() => {
          this.authRefreshPromise = null;
        });
    }

    return this.authRefreshPromise;
  }

  private refreshWithCrossTabLock(requestedSession: RefreshSessionSnapshot): Promise<any> {
    const lockManager = this.window && this.window.navigator && this.window.navigator.locks;
    if (!lockManager || !lockManager.request) {
      return this.refreshLatestSession(requestedSession);
    }

    // Refresh tokens can be rotated by the API. Serialising across tabs prevents
    // two tabs from spending the same token and one subsequently signing out.
    return lockManager.request('adn-session-refresh', () => {
      return this.refreshLatestSession(requestedSession);
    });
  }

  private refreshLatestSession(requestedSession: RefreshSessionSnapshot): Promise<any> {
    const latestSession = Session.obtainData() as RefreshSessionSnapshot;
    if (!this.isAuthenticated() || !latestSession.refreshToken) {
      return Promise.reject(AUTH_EVENT.notAuthenticated);
    }

    // A tab which waited for the lock can use the session written by the tab
    // which refreshed first instead of rotating the new token a second time.
    if (this.sessionChanged(requestedSession, latestSession) && !Session.obtain().isExpired()) {
      return Promise.resolve(latestSession);
    }

    return this.apiAuth.refresh(latestSession.refreshToken).then((res: any) => {
      const currentSession = Session.obtainData() as RefreshSessionSnapshot;
      if (!this.isAuthenticated() || !currentSession.refreshToken) {
        return Promise.reject(AUTH_EVENT.notAuthenticated);
      }

      // An older tab or a sign-in may have replaced the session while this
      // request was in flight. Do not overwrite its newer rotated token.
      if (this.sessionChanged(latestSession, currentSession) && !Session.obtain().isExpired()) {
        return currentSession;
      }

      return Session.obtain().init(
        res.scope || latestSession.scope,
        res.access_token,
        res.refresh_token || latestSession.refreshToken,
        res.username || latestSession.username,
        res.expires_in,
        latestSession.masqueraderUsername,
        latestSession.twoFactorAuthType,
        res.auth_type
      );
    }, (rejection: any) => {
      const currentSession = Session.obtainData() as RefreshSessionSnapshot;
      // If another tab completed a refresh while this request failed, its
      // valid session is authoritative and the failed stale request is benign.
      if (this.isAuthenticated()
        && this.sessionChanged(latestSession, currentSession)
        && !Session.obtain().isExpired()) {
        return currentSession;
      }
      return Promise.reject(rejection);
    });
  }

  private sessionChanged(before: RefreshSessionSnapshot, after: RefreshSessionSnapshot): boolean {
    return before.accessToken !== after.accessToken || before.refreshToken !== after.refreshToken;
  }

  updatePassword(password: any, authToken: any, twoFactorAuthCode: any): any {
    return this.apiAuth.updatePassword(password, authToken, twoFactorAuthCode).then((res: any) => {
      return Session.obtain().init(res.scope, res.access_token, res.refresh_token, res.username, res.expires_in, undefined, undefined, res.auth_type);
    });
  }

  resetPassword(username: any): any {
    return this.apiAuth.resetPassword(username);
  }

  switchUi(username: any, masqUsername: any, accessToken: any, refreshToken: any, expiresIn: any): any {
    return Session.obtain().init(null, accessToken, refreshToken, username, expiresIn, masqUsername);
  }

  signIn(username: any, password: any, masqUsername?: any): any {
    return this.authenticate(username, password, masqUsername).then((res: any) => {
      return Session.obtain().init(
        res.scope,
        res.access_token,
        res.refresh_token,
        masqUsername || username,
        res.expires_in,
        username,
        res.two_factor_auth_type,
        res.auth_type
      );
    });
  }

  signOut(): any {
    UpdateSession.obtain().destroy();
    return Session.obtain().destroy();
  }
}
